import { useQuery } from '@tanstack/react-query';
import { http } from '@repletoryreach/sdk-api';

/** A team as the inbox needs it: enough to name it in a picker. */
export interface InboxTeam {
  id: string;
  name: string;
}

/**
 * The organisation's teams, from identity's `GET /v1/teams` (7 October 2026).
 *
 * Read straight off the http client, because the identity SDK has no teams call of its own yet. Anything
 * that goes wrong (no teams, an older identity without the route, a network hiccup) answers an empty list,
 * and every team control in the inbox hides itself on an empty list: an organisation with no teams sees
 * the inbox exactly as it was.
 */
export function useTeams(): InboxTeam[] {
  const q = useQuery({
    // Its own key: a teams page elsewhere may cache the same route in another shape.
    queryKey: ['inbox', 'teams'],
    queryFn: async () => {
      const res = await http.get<{ data?: unknown }>('/v1/teams').catch(() => null);
      const rows = Array.isArray(res?.data) ? res.data : [];
      return rows
        // A team with no name is left out rather than shown by its id.
        .filter((r): r is { id: string; name: string } =>
          !!r && typeof r === 'object'
          && typeof (r as { id?: unknown }).id === 'string'
          && typeof (r as { name?: unknown }).name === 'string'
          && !!(r as { name: string }).name.trim())
        .map((r) => ({ id: r.id, name: r.name.trim() }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    staleTime: 60_000,
    retry: false
  });
  return q.data ?? [];
}
